/**
 * 渠道管理 API 服务
 */

// 获取认证头
function getAuthHeaders(): HeadersInit {
  const authStore = localStorage.getItem("auth-storage");
  if (authStore) {
    const { state } = JSON.parse(authStore);
    const token = state?.accessToken;
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    }
  }
  return {};
}

// 渠道类型枚举
export enum ChannelType {
  OPENAI = 1,
  API2D = 2,
  AZURE = 3,
  CLOSEAI = 4,
  OPENAI_SB = 5,
  OPENAI_MAX = 6,
  OH_MY_GPT = 7,
  CUSTOM = 8,
  AI_LS = 9,
  AI_PROXY = 10,
  PALM2 = 11,
  API2GPT = 12,
  AIGC2D = 13,
  CLAUDE = 14,
  BAIDU = 15,
  ZHIPU = 16,
  ALIYUN = 17,
  XUNFEI = 18,
  ONE_360 = 19,
  OPENROUTER = 20,
  FASTGPT = 22,
}

// 渠道状态枚举
export enum ChannelStatus {
  ENABLED = 1,
  DISABLED = 2,
}

// 渠道类型选项（用于下拉选择）
export const CHANNEL_OPTIONS = [
  { key: ChannelType.OPENAI, text: 'OpenAI', value: ChannelType.OPENAI, color: 'green' },
  { key: ChannelType.CLAUDE, text: 'Anthropic Claude', value: ChannelType.CLAUDE, color: 'black' },
  { key: ChannelType.AZURE, text: 'Azure OpenAI', value: ChannelType.AZURE, color: 'olive' },
  { key: ChannelType.PALM2, text: 'Google PaLM2', value: ChannelType.PALM2, color: 'orange' },
  { key: ChannelType.BAIDU, text: '百度文心千帆', value: ChannelType.BAIDU, color: 'blue' },
  { key: ChannelType.ALIYUN, text: '阿里通义千问', value: ChannelType.ALIYUN, color: 'orange' },
  { key: ChannelType.XUNFEI, text: '讯飞星火认知', value: ChannelType.XUNFEI, color: 'blue' },
  { key: ChannelType.ZHIPU, text: '智谱 ChatGLM', value: ChannelType.ZHIPU, color: 'violet' },
  { key: ChannelType.ONE_360, text: '360 智脑', value: ChannelType.ONE_360, color: 'blue' },
  { key: ChannelType.CUSTOM, text: '自定义渠道', value: ChannelType.CUSTOM, color: 'pink' },
  { key: ChannelType.FASTGPT, text: '知识库：FastGPT', value: ChannelType.FASTGPT, color: 'blue' },
  { key: ChannelType.AI_PROXY, text: '知识库：AI Proxy', value: ChannelType.AI_PROXY, color: 'purple' },
  { key: ChannelType.OPENROUTER, text: '代理：OpenRouter', value: ChannelType.OPENROUTER, color: 'black' },
  { key: ChannelType.API2D, text: '代理：API2D', value: ChannelType.API2D, color: 'blue' },
  { key: ChannelType.OPENAI_SB, text: '代理：OpenAI-SB', value: ChannelType.OPENAI_SB, color: 'brown' },
  { key: ChannelType.OH_MY_GPT, text: '代理：OhMyGPT', value: ChannelType.OH_MY_GPT, color: 'purple' },
  { key: ChannelType.AI_PROXY, text: '代理：AI Proxy', value: ChannelType.AI_PROXY, color: 'purple' },
  { key: ChannelType.CLOSEAI, text: '代理：CloseAI', value: ChannelType.CLOSEAI, color: 'teal' },
  { key: ChannelType.OPENAI_MAX, text: '代理：OpenAI Max', value: ChannelType.OPENAI_MAX, color: 'violet' },
  { key: ChannelType.AI_LS, text: '代理：AI.LS', value: ChannelType.AI_LS, color: 'yellow' },
  { key: ChannelType.API2GPT, text: '代理：API2GPT', value: ChannelType.API2GPT, color: 'blue' },
  { key: ChannelType.AIGC2D, text: '代理：AIGC2D', value: ChannelType.AIGC2D, color: 'purple' },
];

// 渠道数据接口
export interface Channel {
  id: number;
  type: ChannelType;
  key: string;
  status: ChannelStatus;
  name: string;
  weight: number;
  base_url?: string;
  other?: string;
  models?: string;
  model_mapping?: string;
  group: string;
  balance: number;
  balance_updated_time: number;
  used_quota: number;
  response_time: number;
  test_time: number;
  created_time: number;
  updated_time: number;
}

// 创建/更新渠道请求接口
export interface CreateChannelRequest {
  type: ChannelType;
  key: string;
  status: ChannelStatus;
  name: string;
  weight?: number;
  base_url?: string;
  other?: string;
  models?: string;
  model_mapping?: string;
  group?: string;
}

// API 基础 URL
const API_BASE_URL = '/api';

/**
 * 获取渠道列表
 */
export async function getChannelList(params?: {
  page?: number;
  pageSize?: number;
  keyword?: string;
}): Promise<{ success: boolean; data: Channel[]; message?: string }> {
  try {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.keyword) queryParams.append('keyword', params.keyword);

    const response = await fetch(`${API_BASE_URL}/channel?${queryParams.toString()}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('获取渠道列表失败:', error);
    throw error;
  }
}

/**
 * 获取单个渠道详情
 */
export async function getChannel(channelId: number): Promise<{ success: boolean; data: Channel; message?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel/${channelId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('获取渠道详情失败:', error);
    throw error;
  }
}

/**
 * 创建渠道
 */
export async function createChannel(data: CreateChannelRequest): Promise<{ success: boolean; data: Channel; message?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('创建渠道失败:', error);
    throw error;
  }
}

/**
 * 更新渠道
 */
export async function updateChannel(channelId: number, data: Partial<CreateChannelRequest>): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel/${channelId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('更新渠道失败:', error);
    throw error;
  }
}

/**
 * 删除渠道
 */
export async function deleteChannel(channelId: number): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel/${channelId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('删除渠道失败:', error);
    throw error;
  }
}

/**
 * 启用渠道
 */
export async function enableChannel(channelId: number): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel/${channelId}/enable`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('启用渠道失败:', error);
    throw error;
  }
}

/**
 * 禁用渠道
 */
export async function disableChannel(channelId: number): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel/${channelId}/disable`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('禁用渠道失败:', error);
    throw error;
  }
}

/**
 * 测试渠道
 */
export async function testChannel(channelId: number): Promise<{ success: boolean; message: string; time?: number }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel/${channelId}/test`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('测试渠道失败:', error);
    throw error;
  }
}

/**
 * 测试所有渠道
 */
export async function testAllChannels(): Promise<{ success: boolean; message: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel/test/all`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('测试所有渠道失败:', error);
    throw error;
  }
}

/**
 * 更新渠道余额
 */
export async function updateChannelBalance(channelId: number): Promise<{ success: boolean; message: string; balance?: number }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel/${channelId}/balance/update`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('更新渠道余额失败:', error);
    throw error;
  }
}

/**
 * 更新所有渠道余额
 */
export async function updateAllChannelsBalance(): Promise<{ success: boolean; message: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel/balance/update/all`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('更新所有渠道余额失败:', error);
    throw error;
  }
}




/**
 * 格式化时间戳
 */
export function formatTimestamp(timestamp: number): string {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleString('zh-CN');
}

/**
 * 渲染渠道类型
 */
export function getChannelTypeInfo(type: ChannelType) {
  const option = CHANNEL_OPTIONS.find(opt => opt.value === type);
  return option || { text: '未知类型', color: 'grey' };
}

/**
 * 渲染渠道状态
 */
export function getChannelStatusInfo(status: ChannelStatus) {
  switch (status) {
    case ChannelStatus.ENABLED:
      return { text: '已启用', color: 'green' };
    case ChannelStatus.DISABLED:
      return { text: '已禁用', color: 'red' };
    default:
      return { text: '未知状态', color: 'grey' };
  }
}

/**
 * 渲染余额
 */
export function formatBalance(type: ChannelType, balance: number): string {
  switch (type) {
    case ChannelType.OPENAI:
      return `$${balance.toFixed(2)}`;
    case ChannelType.CLOSEAI:
      return `¥${balance.toFixed(2)}`;
    case ChannelType.CUSTOM:
      return `$${balance.toFixed(2)}`;
    case ChannelType.OPENAI_SB:
      return `¥${(balance / 10000).toFixed(2)}`;
    case ChannelType.AI_PROXY:
      return balance.toString();
    case ChannelType.API2GPT:
      return `¥${balance.toFixed(2)}`;
    case ChannelType.AIGC2D:
      return balance.toString();
    default:
      return '不支持';
  }
}

/**
 * 获取模型列表
 */
export async function getModelList(): Promise<{ success: boolean; data: { id: string }[]; message?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/channel/models`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('获取模型列表失败:', error);
    throw error;
  }
}

/**
 * 获取分组列表
 */
export async function getGroupList(): Promise<{ success: boolean; data: string[]; message?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/group`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('获取分组列表失败:', error);
    throw error;
  }
}

/**
 * 根据渠道类型获取密钥提示
 */
export function getSecretPrompt(type: ChannelType): string {
  switch (type) {
    case ChannelType.BAIDU:
      return '按照如下格式输入：APIKey|SecretKey';
    case ChannelType.XUNFEI:
      return '按照如下格式输入：APPID|APISecret|APIKey';
    case ChannelType.FASTGPT:
      return '按照如下格式输入：APIKey-AppId，例如：fastgpt-0sp2gtvfdgyi4k30jwlgwf1i-64f335d84283f05518e9e041';
    default:
      return '请输入渠道对应的鉴权密钥';
  }
}

/**
 * 根据渠道类型获取默认模型列表
 */
export function getDefaultModels(type: ChannelType): string[] {
  switch (type) {
    case ChannelType.CLAUDE:
      return ['claude-instant-1', 'claude-2', 'claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'];
    case ChannelType.PALM2:
      return ['PaLM-2'];
    case ChannelType.BAIDU:
      return ['ERNIE-Bot', 'ERNIE-Bot-turbo', 'ERNIE-Bot-4', 'Embedding-V1'];
    case ChannelType.ALIYUN:
      return ['qwen-v1', 'qwen-plus-v1', 'qwen-turbo', 'qwen-max', 'text-embedding-v1'];
    case ChannelType.ZHIPU:
      return ['chatglm_pro', 'chatglm_std', 'chatglm_lite', 'glm-4', 'glm-3-turbo'];
    case ChannelType.XUNFEI:
      return ['SparkDesk'];
    case ChannelType.ONE_360:
      return ['360GPT_S2_V9', 'embedding-bert-512-v1', 'embedding_s1_v1', 'semantic_similarity_s1_v1', '360GPT_S2_V9.4'];
    case ChannelType.OPENAI:
    default:
      return ['gpt-3.5-turbo', 'gpt-4', 'gpt-4-turbo', 'gpt-4o'];
  }
}

/**
 * 模型映射示例
 */
export const MODEL_MAPPING_EXAMPLE = {
  'gpt-3.5-turbo-0301': 'gpt-3.5-turbo',
  'gpt-4-0314': 'gpt-4',
  'gpt-4-32k-0314': 'gpt-4-32k'
};

/**
 * 验证 JSON 字符串
 */
export function verifyJSON(str: string): boolean {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

/**
 * 更新渠道接口（支持完整数据）
 */
export interface UpdateChannelRequest {
  id: number;
  type?: ChannelType;
  key?: string;
  status?: ChannelStatus;
  name?: string;
  weight?: number;
  base_url?: string;
  other?: string;
  models?: string;
  model_mapping?: string;
  group?: string;
}
